import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Divider,
  InputAdornment,
  makeStyles,
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import CheckIcon from '@material-ui/icons/Check';
import {
  getSelectedSarauSummary,
  getSelectedSportsSummary,
  formatExtraRoomsBillingLabel,
  formatPhone,
  getCompanionPaymentStatus,
  resolveActiveRoomRules,
} from './config';

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 4,
    color: '#1a3a5c',
  },
  subtitle: {
    textAlign: 'center',
    color: '#5a7a9a',
    marginBottom: theme.spacing(3),
  },
  iconBox: { textAlign: 'center', marginBottom: theme.spacing(2) },
  summaryCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  freeBox: {
    backgroundColor: '#e8f5e9',
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  paymentBox: {
    backgroundColor: '#fff3e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  valuesRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
}));

export default function StepConfirmacao({
  formData,
  updateFormData,
  activityConfig,
  calculateTotal,
}) {
  const classes = useStyles();
  const totals = calculateTotal();
  const hasToPay = totals.total > 0;
  const activeRoomRules = resolveActiveRoomRules(
    activityConfig,
    formData.hotel_id
  );

  const selectedSports = getSelectedSportsSummary(
    formData.modalities,
    activityConfig?.sports || []
  );
  const selectedSarau = getSelectedSarauSummary(
    formData.sarau_activities,
    activityConfig?.sarau || []
  );

  return (
    <Box>
      <Box className={classes.iconBox}>
        <CreditCardIcon style={{ fontSize: 48, color: '#1a3a5c' }} />
      </Box>
      <Typography variant="h5" className={classes.title}>
        Confirmar Inscrição
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Revise suas informações antes de confirmar
      </Typography>

      {/* Dados Pessoais */}
      <Box className={classes.summaryCard}>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 'bold', marginBottom: 8 }}
        >
          Dados Pessoais
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Nome: {formData.name}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              CPF: {formData.cpf}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              E-mail: {formData.email}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Celular: {formatPhone(formData.phone)}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Modalidades */}
      <Box className={classes.summaryCard}>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 'bold', marginBottom: 8 }}
        >
          Modalidades Esportivas
        </Typography>
        {selectedSports.map((sport, i) => (
          <Box key={i} display="flex" alignItems="center" mb={0.5}>
            <CheckIcon
              style={{ fontSize: 16, color: '#4caf50', marginRight: 8 }}
            />
            <Typography variant="body2">{sport}</Typography>
          </Box>
        ))}
      </Box>

      {/* Sarau */}
      <Box className={classes.summaryCard}>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 'bold', marginBottom: 8 }}
        >
          Sarau Cultural
        </Typography>
        {selectedSarau.map((item, i) => (
          <Box key={i} display="flex" alignItems="center" mb={0.5}>
            <CheckIcon
              style={{ fontSize: 16, color: '#4caf50', marginRight: 8 }}
            />
            <Typography variant="body2">{item}</Typography>
          </Box>
        ))}
      </Box>

      {/* Acompanhantes */}
      {formData.companions && formData.companions.length > 0 && (
        <Box className={classes.summaryCard}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 'bold', marginBottom: 8 }}
          >
            Acompanhantes ({formData.companions.length})
          </Typography>
          {formData.companions.map((c, i) => {
            const cSports = getSelectedSportsSummary(
              c.modalities || {},
              activityConfig?.sports || []
            );
            const cSarau = getSelectedSarauSummary(
              c.sarau_activities || {},
              activityConfig?.sarau || []
            );
            return (
              <Box key={i} mb={i < formData.companions.length - 1 ? 1.5 : 0}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {c.name} {getCompanionPaymentStatus(c, activeRoomRules).label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  CPF: {c.cpf}
                </Typography>
                {cSports.length > 0 && (
                  <Box ml={1} mt={0.5}>
                    <Typography
                      variant="caption"
                      style={{ fontWeight: 600, color: '#1a3a5c' }}
                    >
                      Esportes:
                    </Typography>
                    {cSports.map((s, j) => (
                      <Typography
                        key={j}
                        variant="caption"
                        style={{ display: 'block', marginLeft: 8 }}
                      >
                        {s}
                      </Typography>
                    ))}
                  </Box>
                )}
                {cSarau.length > 0 && (
                  <Box ml={1} mt={0.5}>
                    <Typography
                      variant="caption"
                      style={{ fontWeight: 600, color: '#1a3a5c' }}
                    >
                      Sarau:
                    </Typography>
                    {cSarau.map((s, j) => (
                      <Typography
                        key={j}
                        variant="caption"
                        style={{ display: 'block', marginLeft: 8 }}
                      >
                        {s}
                      </Typography>
                    ))}
                  </Box>
                )}
                {i < formData.companions.length - 1 && (
                  <Divider style={{ marginTop: 8 }} />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Valor / Pagamento */}
      {!hasToPay ? (
        <Box className={classes.freeBox}>
          <Box mb={1}>
            <span role="img" aria-label="comemoração" style={{ fontSize: 36 }}>
              🎉
            </span>
          </Box>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Inscrição Gratuita!
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Você não possui acompanhantes pagantes, sua inscrição é gratuita.
          </Typography>
        </Box>
      ) : (
        <Box className={classes.paymentBox}>
          <Typography
            variant="subtitle1"
            style={{
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: 12,
            }}
          >
            Extrato de Pagamento
          </Typography>

          <Box className={classes.valuesRow}>
            <Typography variant="body2">
              {totals.payingCompanions} Acompanhante(s) pagante(s)
            </Typography>
            <Typography variant="body2">
              R$ {totals.companionTotal.toFixed(2).replace('.', ',')}
            </Typography>
          </Box>

          {totals.freeCompanions > 0 && (
            <Box className={classes.valuesRow}>
              <Typography variant="body2">
                {totals.freeCompanions} Acompanhante(s) não pagante(s)
              </Typography>
              <Typography variant="body2">R$ 0,00</Typography>
            </Box>
          )}

          {totals.extraRooms > 0 && (
            <Box className={classes.valuesRow}>
              <Typography variant="body2" style={{ flex: 1, paddingRight: 8 }}>
                {formatExtraRoomsBillingLabel(totals)}
              </Typography>
              <Typography variant="body2" style={{ flexShrink: 0 }}>
                R$ {totals.roomTotal.toFixed(2).replace('.', ',')}
              </Typography>
            </Box>
          )}

          <Divider style={{ margin: '8px 0' }} />
          <Box className={classes.valuesRow}>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
              Valor Total
            </Typography>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
              R$ {totals.total.toFixed(2).replace('.', ',')}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 12 }}
          >
            Informe abaixo o seu melhor e-mail e o número de WhatsApp. Chegará
            através deles um link para o pagamento dos valores listados acima.
            Sua inscrição será finalizada após o pagamento.
          </Typography>

          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="E-mail para pagamento *"
                value={formData.payment_email}
                onChange={(e) =>
                  updateFormData('payment_email', e.target.value)
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="WhatsApp *"
                value={formatPhone(formData.payment_whatsapp)}
                onChange={(e) =>
                  updateFormData(
                    'payment_whatsapp',
                    formatPhone(e.target.value)
                  )
                }
                placeholder="(00) 9 0000-0000"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneOutlinedIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
